import React from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import { bookingShape, historyShape } from 'constants/propTypes';
import Currency from 'utils/Currency';
import { bookingStatus } from 'types/booking';
import { getBookingJourneysURL, getPendingPaymentURL } from 'utils/urls';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { Cell, TableRow, Table, TableHeader } from 'components/ui/StyledTable';
import { ConfirmButton } from 'components/ui/Button';
import { Row } from 'components/shared/Layout';
import { splitPaymentsList, splitPaymentsSum } from 'components/ui/Payments/Payments.utils';
import { CONFIRM_ON_HOLD } from 'store/mutations';
import { useMutation } from '@apollo/client';
import { withRouter } from 'react-router';

const PaymentsOutstanding = ({ booking, history }) => {
  const {
    reference,
    currency,
    status,
    totalPriceToBePaidInBookingCurrency: totalPriceToBePaid,
    expiryTimestamp: bookingExpiryTimestamp,
    onHoldInBookingCurrency: onHold
  } = booking;
  const { depositAmount, expiryTimestamp: onHoldExpiryTimestamp } = onHold;

  const isOnHold = status === bookingStatus.ON_HOLD;
  const hasDepositAmount = depositAmount > 0;
  const balanceAmount = isOnHold ? totalPriceToBePaid : totalPriceToBePaid - depositAmount;
  const balanceTimestamp = isOnHold ? bookingExpiryTimestamp : onHoldExpiryTimestamp;

  const listOfSplitPayments = splitPaymentsList(booking.payments);
  const sumOfSplitPayments = splitPaymentsSum(listOfSplitPayments);

  const isTempOnHoldAddPassenger = status === bookingStatus.TEMPORARILY_ON_HOLD && booking.changeExpiryTimestamp;
  const newDepositAmount = isTempOnHoldAddPassenger ? Currency.subtract(depositAmount, sumOfSplitPayments) : depositAmount;
  const newBalanceAmount = isTempOnHoldAddPassenger ? Currency.subtract(totalPriceToBePaid, newDepositAmount) : balanceAmount;
  const expiryTimestampInProduct = booking.outbound?.[0]?.products?.filter((product) => product.expiryTimestamp)[0]?.expiryTimestamp;
  const depositLabel = isTempOnHoldAddPassenger ? 'paymentsOutstanding.newDeposit' : 'paymentsOutstanding.deposit';

  const totalPricePaid = booking.payments.reduce((acc, payment) => {
    if (payment.paymentStatus === 'S') acc += payment.amountWithCurrency;
    return acc;
  }, 0);

  const isTempOnHoldAndDepositFullyPaid = status === bookingStatus.TEMPORARILY_ON_HOLD && depositAmount <= totalPricePaid;

  const [confirmOnHold] = useMutation(CONFIRM_ON_HOLD, {
    variables: { bookingReference: reference }
  });

  const onPayClick = async () => {
    let url = getPendingPaymentURL(reference, currency);

    if (isTempOnHoldAndDepositFullyPaid) {
      let journeysUrl = getBookingJourneysURL(reference);

      await confirmOnHold();

      history.push(journeysUrl);

      return;
    }

    history.push(url);
  };

  return (
    <>
      <Table data-cy="payments-outstanding">
        <TableHeader columns={14}>
          <Cell span={5}>
            <FormattedMessage id="paymentsOutstanding.type" />
          </Cell>
          <Cell span={5}>
            <FormattedMessage id="paymentsOutstanding.dueBy" />
          </Cell>
          <Cell span={4}>
            <FormattedMessage id="paymentsOutstanding.amount" />
          </Cell>
        </TableHeader>

        {hasDepositAmount && (
          <TableRow columns={14} data-cy="deposit">
            <Cell span={5}>
              <FormattedMessage id={depositLabel} />
            </Cell>
            <Cell span={5}>
              {moment(isTempOnHoldAddPassenger ? expiryTimestampInProduct : bookingExpiryTimestamp).format('ddd Do MMMM YYYY, HH:mm')}
            </Cell>
            <Cell span={4}>
              <FormattedCurrency amount={newDepositAmount} currency={currency} />
            </Cell>
          </TableRow>
        )}

        <TableRow columns={14} data-cy="balance">
          <Cell span={5}>
            <FormattedMessage id="paymentsOutstanding.balance" />
          </Cell>
          <Cell span={5}>{moment(balanceTimestamp).format('ddd Do MMMM YYYY, HH:mm')}</Cell>
          <Cell span={4}>
            <FormattedCurrency amount={newBalanceAmount} currency={currency} />
          </Cell>
        </TableRow>

        <TableRow columns={14} data-cy="total">
          <Cell span={5}>
            <FormattedMessage id="paymentsOutstanding.total" />
          </Cell>
          <Cell span={5} />
          <Cell span={4}>
            <FormattedCurrency amount={totalPriceToBePaid} currency={currency} />
          </Cell>
        </TableRow>
      </Table>

      <Row span={1}>
        <ConfirmButton onClick={onPayClick}>
          <FormattedMessage id="paymentsOutstanding.pay" />
        </ConfirmButton>
      </Row>
    </>
  );
};

PaymentsOutstanding.propTypes = {
  booking: bookingShape.isRequired,
  history: historyShape
};

export default withRouter(PaymentsOutstanding);
